// LoginPage — page chung cho Core + SaaS.
//
// Host responsibility (qua HOST_CONTRACT.md):
//   - `@host/auth.useAuth()` cung cấp { user, loading, login(email,pwd), loginWithGoogle?(arg), listMyTenants?() }
//     - Core: AuthContext gọi /api/auth/login + setUser
//     - SaaS: AuthContext shim gọi authApi.login + setSession + return user
//   - Google button: host render qua prop `googleSlot` vì 2 host dùng 2 lib khác hẳn
//     (SaaS: window.google.accounts.id renderButton; Core: @react-oauth/google auth-code)
//
// Page này KHÔNG biết về axios trực tiếp — chỉ qua useAuth() từ host.
//
// ⚠️ i18n: đây là page ĐẦU TIÊN được chuyển sang Lingui (cụm `pages-auth`). Chuỗi bọc
// bằng macro `<Trans>` / `t` / `msg` — babel biến đổi lúc build, runtime chỉ còn hash id.
// Map hằng số ở module scope KHÔNG gọi được hook, nên dùng `msg` (chỉ tạo descriptor,
// chưa dịch) rồi dịch tại chỗ dùng bằng `i18n._(descriptor)`. Xem docs/I18N.md.

import { useState } from 'react';
import { useNavigate, Navigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useAuth } from '@host/auth';
import LoginShell from './LoginShell.jsx';
import LoginChallenge, { isLoginChallengeError } from '../../components/auth/LoginChallenge.jsx';
import '../../i18n/c/pages-auth.js';

// Map error code BE → message. Host có thể merge thêm code riêng qua `errorMap` prop
// (host truyền chuỗi thường HOẶC descriptor từ msg`…`; cả hai đều chạy — xem translate()).
const DEFAULT_ERROR_MAP = {
  invalid_credentials: msg`Email hoặc mật khẩu không đúng`,
  user_disabled: msg`Tài khoản của bạn đã bị vô hiệu hoá. Liên hệ quản trị viên.`,
  user_not_found: msg`Tài khoản không tồn tại`,
  // Khoá THÀNH VIÊN theo workspace (SaaS 2026-09-07) — khác `tenant_disabled` (cả workspace bị
  // khoá). Phải có ở đây, nếu không mapError rơi xuống `data.error.message` = câu kỹ thuật
  // 'membership locked (manual_console) tenant=…'.
  workspace_locked: msg`Tài khoản của bạn đang bị khoá ở workspace này. Liên hệ quản trị viên workspace.`,
  tenant_disabled: msg`Workspace của bạn đang tạm khoá. Liên hệ quản trị viên.`,
  tenant_suspended: msg`Workspace đang bị tạm dừng. Liên hệ quản trị viên.`,
  tenant_terminated: msg`Workspace đã bị chấm dứt.`,
  org_disabled: msg`Tổ chức của bạn đã bị khoá. Liên hệ chủ tổ chức.`,
  email_not_verified: msg`Email chưa được xác thực. Kiểm tra hộp thư.`,
  rate_limit_exceeded: msg`Quá nhiều yêu cầu, vui lòng thử lại sau 1 phút.`,
  bad_input: msg`Email hoặc mật khẩu không hợp lệ`,
};

// Banner lý do bị đưa về màn login (vd chatApi.forceLogoutAccountDisabled redirect
// `/login?reason=account_disabled`). CHỈ nhận code trong whitelist — cố tình BỎ QUA
// param `message` tự do để URL không thành kênh nhét text lạ vào trang login.
const REASON_BANNERS = {
  account_disabled: msg`Tài khoản của bạn đã bị vô hiệu hoá (nghỉ việc). Vui lòng liên hệ quản trị viên.`,
};

/** Nhận descriptor (msg`…`) hoặc chuỗi thường → ra chuỗi đã dịch. */
function translate(i18n, value) {
  if (!value) return value;
  return typeof value === 'string' ? value : i18n._(value);
}

function mapError(i18n, err, customMap) {
  const code = err?.response?.data?.error?.code;
  const msgText = err?.response?.data?.message || err?.response?.data?.error?.message;
  const status = err?.response?.status;
  const map = { ...DEFAULT_ERROR_MAP, ...(customMap || {}) };
  if (code && map[code]) return translate(i18n, map[code]);
  if (msgText) return msgText;
  if (status === 401) return i18n._(msg`Email hoặc mật khẩu không đúng`);
  if (status === 403) return i18n._(msg`Tài khoản không có quyền đăng nhập`);
  if (status === 429) return i18n._(msg`Quá nhiều yêu cầu, thử lại sau`);
  if (status >= 500) return i18n._(msg`Hệ thống đang lỗi, thử lại sau`);
  if (err?.message?.includes('Network')) return i18n._(msg`Mất kết nối, kiểm tra mạng`);
  return i18n._(msg`Đăng nhập thất bại, vui lòng thử lại`);
}

/**
 * LoginPage — full page, dùng `useAuth()` từ host. Host KHÔNG cần viết JSX.
 *
 * Props:
 * @param {string} logoSrc - đường dẫn logo (host config)
 * @param {string} logoAlt
 * @param {string} brandName - "CNV Work" / "CNV Connect" / ...
 * @param {string} tagline - dòng phụ uppercase (e.g. "CRM · OPERATIONS · AI AGENT")
 * @param {string} subtitle - heading trong card (mặc định: "Chào mừng trở lại", đã dịch)
 * @param {string} subtitleHint - subheading (mặc định: "Đăng nhập để tiếp tục", đã dịch)
 * @param {ReactNode} googleSlot - Google sign-in button (host render — null = hide)
 * @param {string} dividerText - "hoặc" (mặc định, đã dịch)
 * @param {string} forgotHref - link trang "Quên mật khẩu" (null/empty = ẩn link).
 *   Mặc định '/forgot-password' — host PHẢI mount route đó (@cnv/ui ForgotPasswordPage)
 *   và cắm `forgotPassword()` vào useAuth(), nếu không link sẽ dẫn tới trang trắng.
 * @param {string} signupHref - link signup (null/empty = hide footer signup)
 * @param {string} signupText - text trước link, mặc định "Chưa có tài khoản?"
 * @param {string} signupCtaText - text link, mặc định "Đăng ký miễn phí"
 * @param {string} belowCard - text ngoài card cuối (optional)
 * @param {object} errorMap - bổ sung mapping error code (host-specific)
 * @param {string|function} redirectTo - đường dẫn sau login. Function nhận (user, tenants) trả URL.
 *   Mặc định: nếu host có listMyTenants() và user > 1 tenant → '/select-workspace', else '/'.
 * @param {string} variant - 'pink' (default) | 'blue'
 * @param {function} onLocaleChange - host đồng bộ ngôn ngữ vừa chọn lên hồ sơ (optional)
 * @param {string} initialEmail - điền sẵn ô email (optional). Không truyền thì đọc
 *   `location.state.email` — trang đăng ký của Connect dẫn người đã có tài khoản sang đây bằng
 *   `navigate('/login', { state: { email } })`. Cố ý đi router state chứ KHÔNG qua URL: email
 *   nằm trên thanh địa chỉ là vào lịch sử trình duyệt + log máy chủ.
 */
export default function LoginPage({
  logoSrc,
  logoAlt,
  brandName,
  tagline,
  // ⚠️ Mặc định KHÔNG đặt ở chữ ký hàm nữa: giá trị mặc định được tính trước khi hook
  // chạy, mà chuỗi dịch cần `t` từ useLingui(). Để undefined rồi ?? bên dưới.
  subtitle,
  subtitleHint,
  googleSlot,
  // Đường đăng nhập chính, đứng đầu card — chỉ CNV Connect truyền (mã OTP qua SMS).
  otpSlot,
  dividerText,
  forgotHref = '/forgot-password',
  signupHref,
  signupText,
  signupCtaText,
  belowCard,
  errorMap,
  redirectTo,
  variant = 'pink',
  onLocaleChange,
  initialEmail = '',
}) {
  const navigate = useNavigate();
  const auth = useAuth();
  const { t, i18n } = useLingui();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [prefilledEmail] = useState(() => initialEmail
    || (typeof location.state?.email === 'string' ? location.state.email : ''));
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reasonKey = searchParams.get('reason');
  const reasonBanner = REASON_BANNERS[reasonKey] ? i18n._(REASON_BANNERS[reasonKey]) : null;

  // Đã login → redirect.
  if (auth?.user) {
    let target = '/';
    if (typeof redirectTo === 'string') target = redirectTo;
    else if (typeof redirectTo === 'function') target = redirectTo(auth.user, null) || '/';
    return <Navigate to={target} replace />;
  }
  if (auth?.loading) {
    return (
      <LoginShell
        logoSrc={logoSrc}
        logoAlt={logoAlt}
        brandName={brandName}
        tagline={tagline}
        variant={variant}
        onLocaleChange={onLocaleChange}
      >
        <div className="flex items-center justify-center py-4 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin mr-2" />
          <Trans>Đang kiểm tra đăng nhập…</Trans>
        </div>
      </LoginShell>
    );
  }

  // Sau khi có phiên (bước một, hoặc xong bước xác thực 2 lớp / đổi mật khẩu): chào + điều hướng.
  const afterLogin = async (user) => {
    const who = user?.display_name || user?.name || user?.email || '';
    toast.success(t`Xin chào ${who}`);

    let target = '/';
    if (typeof redirectTo === 'function') {
      let tenants = null;
      if (typeof auth.listMyTenants === 'function') {
        try { tenants = await auth.listMyTenants(); } catch { /* ignore */ }
      }
      target = redirectTo(user, tenants) || '/';
    } else if (typeof redirectTo === 'string') {
      target = redirectTo;
    }
    navigate(target);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error(t`Vui lòng nhập email và mật khẩu`, { id: 'login-err' });
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const user = await auth.login(email.trim(), password);
      await afterLogin(user);
    } catch (err) {
      // Cần thêm một bước (xác thực 2 lớp / đặt mật khẩu mới): host có `loginChallenge` đã giữ bước đó ⇒ màn đổi,
      // không toast. Host không có (Core) thì máy chủ của nó không bao giờ trả mã này.
      if (auth.loginChallenge && isLoginChallengeError(err)) return;
      toast.error(mapError(i18n, err, errorMap), { id: 'login-err' });
    } finally {
      setSubmitting(false);
    }
  };

  // Bước hai của đăng nhập (SaaS): thay form bằng màn nhập mã / thiết lập MFA / đặt mật khẩu mới.
  if (auth.loginChallenge?.pending) {
    return (
      <LoginShell
        logoSrc={logoSrc}
        logoAlt={logoAlt}
        brandName={brandName}
        tagline={tagline}
        subtitle={subtitle ?? t`Chào mừng trở lại`}
        subtitleHint={subtitleHint ?? t`Đăng nhập để tiếp tục`}
        variant={variant}
        onLocaleChange={onLocaleChange}
      >
        <LoginChallenge lc={auth.loginChallenge} onSuccess={afterLogin} />
      </LoginShell>
    );
  }

  return (
    <LoginShell
      logoSrc={logoSrc}
      logoAlt={logoAlt}
      brandName={brandName}
      tagline={tagline}
      subtitle={subtitle ?? t`Chào mừng trở lại`}
      subtitleHint={subtitleHint ?? t`Đăng nhập để tiếp tục`}
      variant={variant}
      googleSlot={googleSlot}
      otpSlot={otpSlot}
      dividerText={dividerText}
      onLocaleChange={onLocaleChange}
      footer={signupHref ? (
        <>
          <span className="text-slate-500">{signupText ?? t`Chưa có tài khoản?`}</span>{' '}
          <Link to={signupHref} className="text-blue-600 font-medium hover:underline">
            {signupCtaText ?? t`Đăng ký miễn phí`}
          </Link>
        </>
      ) : null}
      belowCard={belowCard}
    >
      {reasonBanner && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{reasonBanner}</span>
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            <Trans>Email</Trans>
          </label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            {/* placeholder: ví dụ email — PHẢI bọc t``, nếu không màn login tiếng Anh vẫn
                lòi "ban@congty.com" giữa toàn bộ chữ đã dịch. */}
            <input
              type="email"
              required
              autoFocus={!prefilledEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`ban@congty.com`}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="block text-sm font-medium text-slate-700">
              <Trans>Mật khẩu</Trans>
            </label>
            {forgotHref ? (
              // Mang email đang gõ sang trang quên mật khẩu — khỏi bắt gõ lại lần hai.
              <Link to={forgotHref} state={email ? { email } : undefined} className="text-xs text-blue-600 font-medium hover:underline">
                <Trans>Quên mật khẩu?</Trans>
              </Link>
            ) : null}
          </div>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type={showPwd ? 'text' : 'password'}
              required
              autoFocus={!!prefilledEmail}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? t`Ẩn mật khẩu` : t`Hiện mật khẩu`}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? <Trans>Đang đăng nhập...</Trans> : <Trans>Đăng nhập</Trans>}
        </button>
      </form>
    </LoginShell>
  );
}
